/**
 * TabPicker UI Card Component
 * 
 * Reusable card component for content containers and feature displays.
 * Provides consistent styling for various card layouts across the application.
 * 
 * @module TabPicker.UI.Card
 */

import React from 'react';

/**
 * Card component props interface
 * 
 * @interface CardProps
 * @property {string} [className] - Additional CSS classes
 * @property {React.ReactNode} [children] - Card content
 * @property {'default' | 'hover' | 'feature'} [variant] - Card style variant
 * @property {boolean} [noPadding] - Remove default padding
 */
export interface CardProps {
  className?: string;
  children?: React.ReactNode;
  variant?: 'default' | 'hover' | 'feature';
  noPadding?: boolean;
}

/**
 * Card component for content containers
 * 
 * Provides a consistent styled container with shadow and border styling.
 * Supports multiple variants for different use cases.
 * 
 * @param {CardProps} props - Component props
 * @returns {JSX.Element} Card element
 * 
 * @example
 * // Basic card
 * <Card>
 *   <h3>Card Title</h3>
 *   <p>Card content goes here</p>
 * </Card>
 * 
 * @example
 * // Feature card with hover effect
 * <Card variant="hover">
 *   <FeatureIcon />
 *   <CardTitle>Feature Name</CardTitle>
 *   <CardDescription>Feature description</CardDescription>
 * </Card>
 */
export function Card({ 
  className = '', 
  children, 
  variant = 'default',
  noPadding = false 
}: CardProps) {
  const baseClasses = 'bg-white rounded-lg border border-gray-200 shadow-md';
  
  const variantClasses = {
    default: '',
    hover: 'transition-shadow duration-200 hover:shadow-lg cursor-pointer',
    feature: 'border-2'
  };
  
  const paddingClass = noPadding ? '' : 'p-6';
  
  return (
    <div className={`${baseClasses} ${variantClasses[variant]} ${paddingClass} ${className}`}>
      {children}
    </div>
  );
}

/**
 * Card header component props
 */
export interface CardHeaderProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * Card header component
 * 
 * @param {CardHeaderProps} props - Component props
 * @returns {JSX.Element} Card header element
 */
export function CardHeader({ className = '', children }: CardHeaderProps) {
  return (
    <div className={`pb-4 ${className}`}>
      {children}
    </div>
  );
}

/**
 * Card title component props
 */
export interface CardTitleProps {
  className?: string;
  children?: React.ReactNode;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

/**
 * Card title component
 * 
 * @param {CardTitleProps} props - Component props
 * @returns {JSX.Element} Card title element
 */
export function CardTitle({ 
  className = '', 
  children, 
  as: Component = 'h3' 
}: CardTitleProps) {
  return (
    <Component className={`text-lg font-semibold text-gray-900 ${className}`}>
      {children}
    </Component>
  );
}

/**
 * Card description component props
 */
export interface CardDescriptionProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * Card description component
 * 
 * @param {CardDescriptionProps} props - Component props
 * @returns {JSX.Element} Card description element
 */
export function CardDescription({ className = '', children }: CardDescriptionProps) {
  return (
    <p className={`text-gray-600 text-sm mt-2 ${className}`}>
      {children}
    </p>
  );
}

/**
 * Card content component props
 */
export interface CardContentProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * Card content component
 * 
 * @param {CardContentProps} props - Component props
 * @returns {JSX.Element} Card content element
 */
export function CardContent({ className = '', children }: CardContentProps) {
  return (
    <div className={`${className}`}>
      {children}
    </div>
  );
}

/**
 * Card footer component props
 */
export interface CardFooterProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * Card footer component
 * 
 * @param {CardFooterProps} props - Component props
 * @returns {JSX.Element} Card footer element
 */
export function CardFooter({ className = '', children }: CardFooterProps) {
  return (
    <div className={`pt-4 mt-4 border-t border-gray-200 ${className}`}>
      {children}
    </div>
  );
}

/**
 * Feature card component props
 */
export interface FeatureCardProps {
  icon?: React.ReactNode;
  title: string;
  description: string;
  className?: string;
  onClick?: () => void;
}

/**
 * Feature card component for highlighting product features
 * 
 * @param {FeatureCardProps} props - Component props
 * @returns {JSX.Element} Feature card element
 * 
 * @example
 * <FeatureCard
 *   icon="📊"
 *   title="Analyze Odds"
 *   description="Get instant calculations on hold percentages and expected returns."
 * />
 */
export function FeatureCard({ 
  icon, 
  title, 
  description, 
  className = '',
  onClick 
}: FeatureCardProps) {
  return (
    <Card 
      variant={onClick ? 'hover' : 'default'} 
      className={className}
      {...(onClick && { onClick })}
    >
      {icon && (
        <div className="text-4xl mb-4 text-blue-600">{icon}</div>
      )}
      <CardTitle>{title}</CardTitle>
      <CardDescription>{description}</CardDescription>
    </Card>
  );
}